import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines class names with clsx and merges Tailwind classes with tailwind-merge
 * This prevents conflicting Tailwind classes and ensures proper class merging
 *
 * @param {...(string | undefined | null | boolean)} inputs - Class names to combine
 * @returns {string} - Merged class names
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Utility function to format node IDs for display
 * @param {string} id - Node ID to format
 * @returns {string} - Formatted display name
 */
export function formatNodeId(id) {
  return id.replace(/^custom\w+-/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Utility function to validate variable syntax in text
 * @param {string} text - Text to validate
 * @returns {Array} - Array of found variables
 */
export function extractVariables(text) {
  const variableRegex = /\{\{([^}]+)\}\}/g;
  const variables = [];
  let match;

  while ((match = variableRegex.exec(text)) !== null) {
    variables.push({
      name: match[1].trim(),
      start: match.index,
      end: match.index + match[0].length,
      full: match[0]
    });
  }

  return variables;
}

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Generate a unique node ID
 * @param {string} type - Node type prefix
 * @returns {string} - Unique node ID
 */
export function generateNodeId(type) {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 5);
  return `custom${type.charAt(0).toUpperCase() + type.slice(1)}-${timestamp}-${random}`;
}